import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import DatePicker from 'react-datepicker';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import { Dropdown } from '../helpers/Dropdown';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import {
    createScheduleDefinition,
    getScheduleDefinition,
    getScheduleDefinitions,
    updateScheduleDefinition,
} from '../../services/notificationScheduleDefinitionService';
import {
    buildScheduleDefinitionPayload,
    normalizeUtcScheduleDates,
} from '../../utils/notificationScheduleUtils';
import { getText } from '../../i18n';
import styles from './NotificationScheduleDetails.module.scss';
import { CharacterLimitIndicator } from '../helpers/CharacterLimitIndicator';
import { useWindowListener } from '../helpers/useWindowListener';
import { formatDate } from '../helpers/DateUtils';

const FREQUENCIES = [
    { label: getText('msg_d9c768782ea8'), key: 'once' },
    { label: getText('msg_728298d3dbf4'), key: 'daily' },
    { label: getText('msg_158f3da59275'), key: 'weekly' },
    { label: getText('msg_d31edb7b8a94'), key: 'monthly' },
];

const WEEKDAYS = [
    ['0', getText('msg_bc5dd045b862')],
    ['1', getText('msg_932eeb1076c8')],
    ['2', getText('msg_42e43b612a5d')],
    ['3', getText('msg_5656b9b79b03')],
    ['4', getText('msg_76031ddf9245')],
    ['5', getText('msg_d166e844a3f3')],
    ['6', getText('msg_17063c506b81')],
];

const TIMEZONE_OPTIONS = [
    { value: 'America/New_York', label: getText('msg_e1acb4f52a7a') },
    { value: 'America/Chicago', label: getText('msg_91b79ee5e078') },
    { value: 'America/Denver', label: getText('msg_477f7cb7d608') },
    { value: 'America/Phoenix', label: getText('msg_d07cb05f55a7') },
    { value: 'America/Los_Angeles', label: getText('msg_2856e8edb8d9') },
    { value: 'America/Anchorage', label: getText('msg_be9294933972') },
    { value: 'Pacific/Honolulu', label: getText('msg_c2b265b1776e') },
    { value: 'America/Puerto_Rico', label: getText('msg_7f969f4e765b') },
    { value: 'Pacific/Guam', label: getText('msg_f4e031163e8e') },
    { value: 'Pacific/Pago_Pago', label: getText('msg_34589e2a8b67') },
    { value: 'Pacific/Wake', label: getText('msg_b1fb3b41beff') },
];

const MONTHS = [
    getText('msg_7a22d73d336a'),
    getText('msg_5c3a35ef85f2'),
    getText('msg_433632ea5cd6'),
    getText('msg_a0393902db1f'),
    getText('msg_c94f479833c5'),
    getText('msg_a9db90676169'),
    getText('msg_df97a42549e5'),
    getText('msg_69d97c5797dc'),
    getText('msg_1c542e79c9b4'),
    getText('msg_87206ae23634'),
    getText('msg_3c5bf776f5ef'),
    getText('msg_65640c6577c9'),
];

const NOW_TIME_VALUE = 'Now';

const TIME_OPTIONS = [
    { value: NOW_TIME_VALUE, label: getText('msg_e3b82040565b') },
    ...Array.from({ length: 48 }, (_, index) => {
        const totalMinutes = (7 * 60 + index * 30) % (24 * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const displayHours = hours % 12 || 12;
        const period = hours >= 12 ? 'PM' : 'AM';

        return {
            value: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
            label: `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`,
        };
    }),
];

const ORDINAL_OPTIONS = [
    { value: 'first', label: getText('msg_916a78d701de') },
    { value: 'second', label: getText('msg_2a4bcae654f2') },
    { value: 'third', label: getText('msg_efb3a3216290') },
    { value: 'fourth', label: getText('msg_7a3a4a42216f') },
    { value: 'last', label: getText('msg_d1c69a859a96') },
];

const MONTHLY_WEEKDAY_OPTIONS = [
    { value: '0', label: getText('msg_bc5dd045b862') },
    { value: '1', label: getText('msg_932eeb1076c8') },
    { value: '2', label: getText('msg_42e43b612a5d') },
    { value: '3', label: getText('msg_5656b9b79b03') },
    { value: '4', label: getText('msg_76031ddf9245') },
    { value: '5', label: getText('msg_d166e844a3f3') },
    { value: '6', label: getText('msg_17063c506b81') },
];

const FREQUENCY_OPTIONS = FREQUENCIES.map(option => ({
    label: option.label,
    value: option.key,
}));

const END_MODE_OPTIONS = [
    { label: getText('msg_79ba5e1b3f99'), value: 'after' },
    { label: getText('msg_e0049a66519c'), value: 'on' },
];

const clampInteger = (value, min, max, fallback = min) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return fallback;
    return Math.min(Math.max(parsed, min), max);
};

const getOrdinalWeekdayOfMonth = ({
    year,
    month,
    weekday,
    ordinal,
    hours,
    minutes,
}) => {
    const matchingDates = [];

    for (let day = 1; day <= 31; day += 1) {
        const candidate = new Date(
            Date.UTC(year, month, day, hours, minutes, 0, 0),
        );

        if (candidate.getUTCMonth() !== month) break;
        if (candidate.getUTCDay() === weekday) matchingDates.push(candidate);
    }

    if (!matchingDates.length) return null;

    switch (ordinal) {
        case 'first':
            return matchingDates[0];
        case 'second':
            return matchingDates[1] || null;
        case 'third':
            return matchingDates[2] || null;
        case 'fourth':
            return matchingDates[3] || null;
        case 'last':
            return matchingDates[matchingDates.length - 1];
        default:
            return matchingDates[0];
    }
};

const getBrowserTimezone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';

const pad = value => String(value).padStart(2, '0');

const dateInputValue = date =>
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

const getDropdownLabel = (options, value) =>
    options.find(option => option.value === value)?.label || value || '';

const addMonthsClamped = (date, months) => {
    const result = new Date(date);
    const originalDay = result.getUTCDate();
    result.setUTCDate(1);
    result.setUTCMonth(result.getUTCMonth() + months);
    const lastDay = new Date(
        Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
    ).getUTCDate();
    result.setUTCDate(Math.min(originalDay, lastDay));
    return result;
};

const parseZonedInput = (dateValue, timeValue = '00:00', timezone = 'UTC') => {
    const wallClock = `${dateValue}T${timeValue}:00`;
    return fromZonedTime(wallClock, timezone);
};

const resolveScheduleTime = timeValue => {
    if (timeValue !== NOW_TIME_VALUE) {
        return timeValue;
    }

    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const toUtcDateTimeInputValue = isoString => {
    if (!isoString) return '';

    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
        date.getUTCDate(),
    )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
};

const fromUtcDateTimeInputValue = value => {
    if (!value) return null;

    const [datePart, timePart = '00:00'] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    return new Date(
        Date.UTC(year, month - 1, day, hours, minutes, 0, 0),
    ).toISOString();
};

const generateOccurrences = ({
    frequency,
    startDate,
    time,
    timezone,
    endDate,
    endsMode,
    occurrenceCount,
    weekdays,
    monthDay,
    dailyMode,
    dailyInterval,
    monthlyMode,
    monthlyInterval,
    monthlyOrdinal,
    monthlyWeekday,
}) => {
    const resolveTimeForOccurrenceGeneration = timeValue =>
        resolveScheduleTime(timeValue);

    if (!startDate || !time || !timezone) return [];

    const start = parseZonedInput(
        startDate,
        resolveTimeForOccurrenceGeneration(time),
        timezone,
    );
    if (Number.isNaN(start.getTime())) return [];

    const limit =
        endsMode === 'on' && endDate
            ? parseZonedInput(endDate, '23:59', timezone)
            : null;

    const maxOccurrences =
        frequency === 'once'
            ? 1
            : Math.min(Math.max(Number(occurrenceCount) || 1, 1), 366);

    const dates = [];

    if (frequency === 'once') return [start.toISOString()];

    if (frequency === 'daily') {
        if (dailyMode === 'weekday') {
            const cursor = new Date(start);

            while (dates.length < maxOccurrences) {
                if (limit && cursor > limit) break;

                const day = cursor.getUTCDay();
                if (day !== 0 && day !== 6) {
                    dates.push(new Date(cursor).toISOString());
                }

                cursor.setUTCDate(cursor.getUTCDate() + 1);
            }
        } else {
            const interval = clampInteger(dailyInterval, 1, 366, 1);
            const cursor = new Date(start);

            while (dates.length < maxOccurrences) {
                if (limit && cursor > limit) break;

                dates.push(new Date(cursor).toISOString());
                cursor.setUTCDate(cursor.getUTCDate() + interval);
            }
        }
    } else if (frequency === 'weekly') {
        const selected = new Set(
            (weekdays.length ? weekdays : [String(start.getUTCDay())]).map(
                Number,
            ),
        );

        const cursor = new Date(start);

        while (dates.length < maxOccurrences) {
            if (limit && cursor > limit) break;

            if (selected.has(cursor.getUTCDay())) {
                dates.push(new Date(cursor).toISOString());
            }

            cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
    } else {
        const interval = clampInteger(monthlyInterval, 1, 12, 1);

        if (monthlyMode === 'ordinal') {
            let monthOffset = 0;

            while (dates.length < maxOccurrences) {
                const seed = addMonthsClamped(start, monthOffset);

                const candidate = getOrdinalWeekdayOfMonth({
                    year: seed.getUTCFullYear(),
                    month: seed.getUTCMonth(),
                    weekday: clampInteger(
                        monthlyWeekday,
                        0,
                        6,
                        start.getUTCDay(),
                    ),
                    ordinal: monthlyOrdinal,
                    hours: start.getUTCHours(),
                    minutes: start.getUTCMinutes(),
                });

                if (!candidate) {
                    monthOffset += interval;
                    continue;
                }

                if (candidate < start) {
                    monthOffset += interval;
                    continue;
                }

                if (limit && candidate > limit) break;

                dates.push(candidate.toISOString());
                monthOffset += interval;
            }
        } else {
            const desiredDay = clampInteger(
                monthDay,
                1,
                31,
                start.getUTCDate(),
            );
            let monthOffset = 0;

            while (dates.length < maxOccurrences) {
                const candidate = addMonthsClamped(start, monthOffset);
                const lastDayOfMonth = new Date(
                    Date.UTC(
                        candidate.getUTCFullYear(),
                        candidate.getUTCMonth() + 1,
                        0,
                    ),
                ).getUTCDate();

                candidate.setUTCDate(Math.min(desiredDay, lastDayOfMonth));

                if (candidate < start) {
                    monthOffset += interval;
                    continue;
                }

                if (limit && candidate > limit) break;

                dates.push(candidate.toISOString());
                monthOffset += interval;
            }
        }
    }

    return normalizeUtcScheduleDates(dates);
};

const getResponseData = response => response?.data || response || {};

const getFrequencyKey = data => {
    if (Number(data.notificationFrequencyId) === 2) return 'once';

    const frequencyName = String(
        data.frequencyName ||
            data.notificationFrequencyName ||
            data.frequency ||
            '',
    ).toLowerCase();
    if (frequencyName.includes('weekly')) return 'weekly';
    if (frequencyName.includes('monthly')) return 'monthly';
    if (frequencyName.includes('daily')) return 'daily';
    return 'daily';
};

const getInferredWeekdays = schedules => [
    ...new Set(schedules.map(value => String(new Date(value).getUTCDay()))),
];
const getInferredMonthDay = schedules =>
    schedules.length ? String(new Date(schedules[0]).getUTCDate()) : '';

const NotificationScheduleDetails = () => {
    const { notificationId, scheduleDefinitionId } = useParams();
    const navigate = useNavigate();
    const tenantId = +window.localStorage.getItem('tenantId');
    const isEditing = Boolean(scheduleDefinitionId);
    const canShowHistory = isEditing;
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [definitionLoadError, setDefinitionLoadError] = useState(false);
    const [scheduleDefinitions, setScheduleDefinitions] = useState([]);
    const [scheduleDefinitionsLoading, setScheduleDefinitionsLoading] =
        useState(true);
    const [scheduleDefinitionsError, setScheduleDefinitionsError] =
        useState(false);
    const [scheduleFieldsChanged, setScheduleFieldsChanged] = useState(false);
    const [form, setForm] = useState({
        name: '',
        frequency: 'once',
        startDate: dateInputValue(new Date()),
        time: '09:00',
        timezone: getBrowserTimezone(),
        endsMode: 'after',
        endDate: '',
        occurrenceCount: '1',
        weekdays: [],
        monthDay: '',
        isEnabled: true,
        notificationGroups: [],
        broadcastAll: false,
        notificationSchedules: [],

        dailyMode: 'interval',
        dailyInterval: '1',

        monthlyMode: 'day',
        monthlyInterval: '1',
        monthlyOrdinal: 'first',
        monthlyWeekday: String(new Date().getUTCDay()),
    });
    const [populationGroups, setPopulationGroups] = useState([]);
    const [populationGroupsLoading, setPopulationGroupsLoading] =
        useState(true);
    const [isOccurrenceDrawerOpen, setIsOccurrenceDrawerOpen] = useState(false);
    const [drawerOccurrences, setDrawerOccurrences] = useState([]);
    const [reviewedOccurrences, setReviewedOccurrences] = useState([]);
    const [hasManualOccurrenceEdits, setHasManualOccurrenceEdits] =
        useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const historyRef = useRef(null);
    const [historyMeta, setHistoryMeta] = useState({
        lastEditedAt: null,
        editedBy: '',
    });

    const loadDefinition = useCallback(async () => {
        if (!isEditing) return;
        setDefinitionLoadError(false);
        try {
            const response = await getScheduleDefinition(
                tenantId,
                notificationId,
                scheduleDefinitionId,
            );
            const data = getResponseData(response);
            setHistoryMeta({
                lastEditedAt: data.updatedAt || data.createdAt || null,
                editedBy: data.updatedBy || data.createdBy || '—',
            });
            const schedules = normalizeUtcScheduleDates(
                data.notificationSchedules || data.schedules || [],
            );
            const first = schedules[0] ? new Date(schedules[0]) : new Date();
            setForm(previous => {
                const resolvedTimezone =
                    data.timezone || previous.timezone || getBrowserTimezone();

                return {
                    ...previous,
                    name: data.name || '',
                    frequency: getFrequencyKey(data),
                    startDate: formatInTimeZone(
                        first,
                        resolvedTimezone,
                        'yyyy-MM-dd',
                    ),
                    time: formatInTimeZone(first, resolvedTimezone, 'HH:mm'),
                    timezone: resolvedTimezone,
                    endsMode: 'after',
                    endDate: '',
                    occurrenceCount: String(schedules.length || 1),
                    weekdays:
                        getFrequencyKey(data) === 'weekly'
                            ? getInferredWeekdays(schedules)
                            : previous.weekdays,
                    monthDay:
                        getFrequencyKey(data) === 'monthly'
                            ? getInferredMonthDay(schedules)
                            : previous.monthDay,
                    isEnabled:
                        data.isEnabled ??
                        data.enabled ??
                        data.status !== 'Disabled',
                    notificationGroups: data.notificationGroups || [],
                    broadcastAll: data.broadcastAll ?? false,
                    notificationSchedules: schedules,

                    dailyMode: 'interval',
                    dailyInterval: '1',

                    monthlyMode: 'day',
                    monthlyInterval: '1',
                    monthlyOrdinal: 'first',
                    monthlyWeekday: schedules.length
                        ? String(new Date(schedules[0]).getUTCDay())
                        : previous.monthlyWeekday,
                };
            });
        } catch (loadError) {
            console.error('Failed to load schedule definition', loadError);
            setDefinitionLoadError(true);
            setError(getText('msg_7bebc59af6d2'));
        } finally {
            setIsLoading(false);
        }
    }, [isEditing, notificationId, scheduleDefinitionId, tenantId]);

    const loadPopulationGroups = useCallback(async () => {
        setPopulationGroupsLoading(true);
        try {
            const groups = await fetchPopulationGroups();
            setPopulationGroups(groups?.viewModels || groups || []);
        } catch (loadError) {
            console.error('Failed to load population groups', loadError);
            Toast.error(getText('msg_3d07ef3f2956'));
        } finally {
            setPopulationGroupsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPopulationGroups();
    }, [loadPopulationGroups]);

    const loadDefinitions = useCallback(async () => {
        setScheduleDefinitionsLoading(true);
        setScheduleDefinitionsError(false);
        try {
            const response = await getScheduleDefinitions(
                tenantId,
                notificationId,
            );
            const rows = Array.isArray(response)
                ? response
                : response?.viewModels ||
                  response?.items ||
                  response?.data ||
                  [];
            setScheduleDefinitions(rows);
        } catch (loadError) {
            console.error(
                'Failed to load existing schedule definitions',
                loadError,
            );
            setScheduleDefinitionsError(true);
            Toast.error(getText('msg_1fe3f4640d39'));
        } finally {
            setScheduleDefinitionsLoading(false);
        }
    }, [notificationId, tenantId]);

    useEffect(() => {
        loadDefinition();
    }, [loadDefinition]);

    useEffect(() => {
        loadDefinitions();
    }, [loadDefinitions]);

    useWindowListener('mousedown', event => {
        if (historyRef.current && !historyRef.current.contains(event.target)) {
            setIsHistoryOpen(false);
        }
    });

    const update = (field, value) => {
        setForm(previous => ({ ...previous, [field]: value }));
        if (
            [
                'frequency',
                'startDate',
                'time',
                'timezone',
                'endDate',
                'endsMode',
                'occurrenceCount',
                'weekdays',
                'monthDay',
                'dailyMode',
                'dailyInterval',
                'monthlyMode',
                'monthlyInterval',
                'monthlyOrdinal',
                'monthlyWeekday',
            ].includes(field)
        ) {
            setScheduleFieldsChanged(true);
        }
    };

    const handleBroadcastAllChange = checked => {
        setForm(previous => ({
            ...previous,
            broadcastAll: checked,
        }));
    };

    const occurrences = useMemo(() => {
        if (
            isEditing &&
            form.notificationSchedules.length &&
            !scheduleFieldsChanged
        ) {
            return form.notificationSchedules;
        }
        return generateOccurrences(form);
    }, [form, isEditing, scheduleFieldsChanged]);

    const finalOccurrences = useMemo(
        () =>
            hasManualOccurrenceEdits
                ? normalizeUtcScheduleDates(reviewedOccurrences)
                : occurrences,
        [hasManualOccurrenceEdits, reviewedOccurrences, occurrences],
    );

    const selectedCalendarDate = form.startDate
        ? new Date(`${form.startDate}T12:00:00`)
        : null;

    const datesArray = finalOccurrences.map(value => new Date(value));

    const handleOnClickDate = date => {
        if (!date) return;

        update('startDate', dateInputValue(date));
    };

    const months = MONTHS;

    const years = Array.from({ length: 11 }, (_, index) => {
        const currentYear = new Date().getFullYear();
        return currentYear + index;
    });

    const getMonth = date => date.getMonth();
    const getYear = date => date.getFullYear();

    useEffect(() => {
        if (scheduleFieldsChanged && hasManualOccurrenceEdits) {
            setHasManualOccurrenceEdits(false);
            setReviewedOccurrences([]);
        }
    }, [scheduleFieldsChanged, hasManualOccurrenceEdits]);

    const selectedPopulationGroups = useMemo(
        () =>
            populationGroups.filter(group =>
                form.notificationGroups.includes(group.id),
            ),
        [populationGroups, form.notificationGroups],
    );

    const openOccurrenceDrawer = () => {
        setDrawerOccurrences([
            ...(finalOccurrences.length ? finalOccurrences : []),
        ]);
        setIsOccurrenceDrawerOpen(true);
    };

    const updateDrawerOccurrence = (index, nextValue) => {
        const parsed = fromUtcDateTimeInputValue(nextValue);

        setDrawerOccurrences(previous =>
            previous.map((item, itemIndex) =>
                itemIndex === index ? parsed || item : item,
            ),
        );
    };

    const removeDrawerOccurrence = index => {
        setDrawerOccurrences(previous =>
            previous.filter((_, itemIndex) => itemIndex !== index),
        );
    };

    const addDrawerOccurrence = () => {
        const fallback =
            finalOccurrences[finalOccurrences.length - 1] ||
            occurrences[occurrences.length - 1] ||
            parseZonedInput(
                form.startDate,
                form.time,
                form.timezone,
            ).toISOString();

        setDrawerOccurrences(previous => [...previous, fallback]);
    };

    const applyOccurrenceReview = () => {
        const normalized = normalizeUtcScheduleDates(
            drawerOccurrences.filter(Boolean),
        );

        setReviewedOccurrences(normalized);
        setHasManualOccurrenceEdits(true);
        setIsOccurrenceDrawerOpen(false);
    };

    const validate = () => {
        if (!form.name.trim()) return getText('msg_86c810d9c46c');
        if (form.name.trim().length > 50) return getText('msg_07d6dced36c2');
        const normalizedName = form.name.trim().toLocaleLowerCase();
        const duplicate = scheduleDefinitions.some(definition => {
            const definitionId =
                definition.id ?? definition.notificationScheduleDefinitionId;
            return (
                String(definitionId ?? '') !==
                    String(scheduleDefinitionId ?? '') &&
                String(definition.name || '')
                    .trim()
                    .toLocaleLowerCase() === normalizedName
            );
        });
        if (duplicate) return getText('msg_9c8b44e6739f');
        if (!form.broadcastAll && !form.notificationGroups.length) {
            return getText('msg_c4fb0d44c0e8');
        }
        if (!form.startDate || !form.time) return getText('msg_61016827a3fe');
        if (
            form.frequency !== 'once' &&
            form.endsMode === 'on' &&
            !form.endDate
        ) {
            return getText('msg_da48f8901ee3');
        }
        if (
            form.endsMode === 'on' &&
            form.endDate &&
            form.endDate < form.startDate
        ) {
            return getText('msg_1d27c710ec62');
        }

        if (form.endsMode === 'after') {
            const occurrenceCount = Number(form.occurrenceCount);
            if (
                !Number.isInteger(occurrenceCount) ||
                occurrenceCount < 1 ||
                occurrenceCount > 366
            ) {
                return getText('msg_6ce4095a3dff');
            }
        }
        if (form.frequency === 'daily' && form.dailyMode === 'interval') {
            const interval = Number(form.dailyInterval);
            if (!Number.isInteger(interval) || interval < 1 || interval > 366) {
                return getText('msg_a9582d26b703');
            }
        }

        if (form.frequency === 'weekly' && !form.weekdays.length) {
            return getText('msg_b2f841ba6f33');
        }

        if (form.frequency === 'monthly') {
            const interval = Number(form.monthlyInterval);
            if (!Number.isInteger(interval) || interval < 1 || interval > 12) {
                return getText('msg_2ef032058448');
            }

            if (form.monthlyMode === 'day') {
                const monthlyDay = Number(form.monthDay);
                if (
                    !Number.isInteger(monthlyDay) ||
                    monthlyDay < 1 ||
                    monthlyDay > 31
                ) {
                    return getText('msg_d01d13a19a24');
                }
            }

            if (form.monthlyMode === 'ordinal') {
                if (!form.monthlyOrdinal) {
                    return getText('msg_1f075c0a17a0');
                }

                if (
                    form.monthlyWeekday === '' ||
                    form.monthlyWeekday === null ||
                    form.monthlyWeekday === undefined
                ) {
                    return getText('msg_b17ad02214c2');
                }
            }
        }
        if (!finalOccurrences.length) return getText('msg_f9b58624e25b');
        return '';
    };

    const handleSave = async event => {
        event.preventDefault();
        if (scheduleDefinitionsLoading) {
            Toast.error(getText('msg_db4b1b35544c'));
            return;
        }
        if (scheduleDefinitionsError) {
            Toast.error(getText('msg_1fe3f4640d39'));
            return;
        }
        const validationError = validate();
        if (validationError) {
            Toast.error(validationError);
            return;
        }
        setIsSaving(true);
        const payload = {
            ...buildScheduleDefinitionPayload({
                id: isEditing ? Number(scheduleDefinitionId) : 0,
                notificationId: Number(notificationId),
                name: form.name.trim(),
                notificationFrequencyId: form.frequency === 'once' ? 2 : 1,
                isEnabled: form.isEnabled,
                notificationSchedules: finalOccurrences,
            }),
            notificationGroups: form.notificationGroups,
            broadcastAll: form.broadcastAll,
        };
        try {
            if (isEditing) {
                await updateScheduleDefinition(
                    tenantId,
                    notificationId,
                    scheduleDefinitionId,
                    payload,
                );
                Toast.success(getText('msg_da90028ad789'));
            } else {
                await createScheduleDefinition(
                    tenantId,
                    notificationId,
                    payload,
                );
                Toast.success(getText('msg_c18a4d939661'));
            }
            navigate(ROUTE_BUILDERS.notificationScheduleList(notificationId));
        } catch (saveError) {
            console.error('Failed to save schedule definition', saveError);
            Toast.error(
                saveError?.response?.data?.message ||
                    getText('msg_6fdde42e05ac'),
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading)
        return <Spinner width={50} className={styles.loadingSpinner} />;

    const pageTitle = isEditing
        ? getText('msg_ea2815370c10', { scheduleName: form.name || '' })
        : getText('msg_6c158ab2b9dc');

    return (
        <div className="page-content">
            <div className={`card ${styles.card}`}>
                <div className={`card-body px-0 py-4`}>
                    <div className="d-flex justify-content-between align-items-center position-relative pb-3">
                        <h2 className="page-title float-start ms-4 m-0 col-4">
                            {pageTitle}
                        </h2>

                        <div className={`col-auto ${styles.headerContent}`}>
                            <button
                                type="submit"
                                form="notificationScheduleDetailsForm"
                                className={styles.headerActionButton}
                                disabled={
                                    isSaving ||
                                    scheduleDefinitionsLoading ||
                                    scheduleDefinitionsError ||
                                    definitionLoadError
                                }
                            >
                                <i className="bi bi-check-lg me-2" />
                                {isSaving
                                    ? getText('msg_ae7e887517b0')
                                    : scheduleDefinitionsLoading
                                      ? getText('msg_1abe79290afc')
                                      : getText('msg_efc007a393f6')}
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    navigate(
                                        ROUTE_BUILDERS.notificationScheduleList(
                                            notificationId,
                                        ),
                                    )
                                }
                                className={styles.headerActionButton}
                                disabled={isSaving}
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </button>
                        </div>
                    </div>
                    {error && (
                        <div className={styles.error} role="alert">
                            <span>{error}</span>
                            {definitionLoadError && (
                                <button
                                    className="button-base ing-button-secondary-left"
                                    type="button"
                                    onClick={loadDefinition}
                                    disabled={isLoading}
                                >
                                    {getText('msg_042c862e4467')}
                                </button>
                            )}
                        </div>
                    )}
                    <form
                        id="notificationScheduleDetailsForm"
                        onSubmit={handleSave}
                        className={styles.scheduleForm}
                    >
                        <div className={styles.nameSection}>
                            <label className={styles.centeredField}>
                                <span className={styles.fieldLabel}>
                                    {getText('msg_709a23220f2c')}{' '}
                                    <span className={styles.requiredMark}>
                                        *
                                    </span>
                                </span>
                                <input
                                    className="form-control"
                                    value={form.name}
                                    onChange={event =>
                                        update('name', event.target.value)
                                    }
                                    maxLength={50}
                                    autoFocus
                                />
                            </label>

                            <div className={styles.nameMeta}>
                                <CharacterLimitIndicator
                                    maxChars={50}
                                    inputStr={form.name || ''}
                                />

                                {canShowHistory && (
                                    <span
                                        className={styles.historyAnchor}
                                        ref={historyRef}
                                    >
                                        <button
                                            type="button"
                                            className={styles.linkButton}
                                            onClick={() =>
                                                setIsHistoryOpen(prev => !prev)
                                            }
                                        >
                                            {getText('msg_dd8f3bf9d92a')}
                                        </button>

                                        {isHistoryOpen && (
                                            <div
                                                className={
                                                    styles.historyPopover
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.historyTitle
                                                    }
                                                >
                                                    {getText(
                                                        'msg_90ccd6497400',
                                                    )}
                                                </div>

                                                <div
                                                    className={
                                                        styles.historyGrid
                                                    }
                                                >
                                                    <div>
                                                        <div
                                                            className={
                                                                styles.historyLabel
                                                            }
                                                        >
                                                            {getText(
                                                                'msg_0c79adcf41ec',
                                                            )}
                                                        </div>
                                                        <div
                                                            className={
                                                                styles.historyValue
                                                            }
                                                        >
                                                            {historyMeta.lastEditedAt
                                                                ? formatDate(
                                                                      historyMeta.lastEditedAt,
                                                                  )
                                                                : '—'}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div
                                                            className={
                                                                styles.historyLabel
                                                            }
                                                        >
                                                            {getText(
                                                                'msg_6180d5db3ed9',
                                                            )}
                                                        </div>
                                                        <div
                                                            className={
                                                                styles.historyValue
                                                            }
                                                        >
                                                            {historyMeta.editedBy ||
                                                                '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className={styles.populationGroupSection}>
                            <div className={styles.populationGroupHeader}>
                                <div className="file-form-label">
                                    <span className={styles.fieldLabel}>
                                        {getText('msg_e744dd6b3c48')}{' '}
                                        <span className={styles.requiredMark}>
                                            *
                                        </span>
                                    </span>
                                </div>
                            </div>

                            <SearchableDropdown
                                placeholderLabel={getText('msg_434532cbf42f')}
                                options={populationGroups}
                                optionIDField="id"
                                savedOptions={selectedPopulationGroups}
                                savedOptionIDField="id"
                                updateOptions={selected =>
                                    update(
                                        'notificationGroups',
                                        (selected || []).map(group => group.id),
                                    )
                                }
                                disabled={populationGroupsLoading}
                            />

                            <div className={styles.broadcastToggleRow}>
                                <label className={styles.inlineCheckboxLabel}>
                                    <input
                                        type="checkbox"
                                        className={styles.broadcastCheckbox}
                                        checked={form.broadcastAll}
                                        onChange={event =>
                                            handleBroadcastAllChange(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                    {getText('msg_57040b84d50a')}
                                </label>
                            </div>
                        </div>

                        <div className={styles.singleFieldRow}>
                            <div className={styles.singleFieldLabel}>
                                <span className={styles.fieldLabel}>
                                    {getText('msg_89836a870e44')}{' '}
                                    <span className={styles.requiredMark}>
                                        *
                                    </span>
                                </span>
                                <Dropdown
                                    defaultValue={getDropdownLabel(
                                        FREQUENCY_OPTIONS,
                                        form.frequency,
                                    )}
                                    options={FREQUENCY_OPTIONS}
                                    onChange={value =>
                                        update('frequency', value)
                                    }
                                    height="48px"
                                />
                            </div>
                        </div>

                        {form.frequency !== 'once' && (
                            <div className={styles.scheduleToolsSection}>
                                <div className={styles.calendarInner}>
                                    <div className={styles.calendarSection}>
                                        <div
                                            className={
                                                styles.customDatePickerWidth
                                            }
                                        >
                                            <DatePicker
                                                selected={selectedCalendarDate}
                                                highlightDates={datesArray}
                                                calendarClassName="react-datepicker-wrapper"
                                                onChange={handleOnClickDate}
                                                dateFormat={[
                                                    'EEEE, MMM dd yyyy',
                                                    'MM-dd-yyyy',
                                                    'MM/dd/yyyy',
                                                    'MMM dd yyyy',
                                                ]}
                                                formatWeekDay={nameOfDay =>
                                                    nameOfDay.substr(0, 3)
                                                }
                                                minDate={new Date()}
                                                maxDate={
                                                    datesArray.length > 0
                                                        ? new Date(
                                                              new Date(
                                                                  datesArray[0],
                                                              ).getFullYear(),
                                                              new Date(
                                                                  datesArray[0],
                                                              ).getMonth() + 12,
                                                              new Date(
                                                                  datesArray[0],
                                                              ).getDate(),
                                                          )
                                                        : new Date(
                                                              new Date(
                                                                  form.startDate,
                                                              ).getFullYear(),
                                                              new Date(
                                                                  form.startDate,
                                                              ).getMonth() + 12,
                                                              new Date(
                                                                  form.startDate,
                                                              ).getDate(),
                                                          )
                                                }
                                                inline
                                                renderCustomHeader={({
                                                    date,
                                                    changeMonth,
                                                    changeYear,
                                                    decreaseMonth,
                                                    increaseMonth,
                                                    increaseYear,
                                                    decreaseYear,
                                                    prevMonthButtonDisabled,
                                                    nextMonthButtonDisabled,
                                                }) => (
                                                    <div
                                                        className={
                                                            styles.calendarHeader
                                                        }
                                                    >
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.calendarNavButton
                                                            }
                                                            onClick={
                                                                decreaseYear
                                                            }
                                                            disabled={
                                                                prevMonthButtonDisabled
                                                            }
                                                        >
                                                            <i className="bi bi-chevron-double-left" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.calendarNavButton
                                                            }
                                                            onClick={
                                                                decreaseMonth
                                                            }
                                                            disabled={
                                                                prevMonthButtonDisabled
                                                            }
                                                        >
                                                            <i className="bi bi-chevron-left" />
                                                        </button>

                                                        <div
                                                            className={`${styles.calendarSelectWrapper} ${styles.calendarSelectWrapperStart}`}
                                                        >
                                                            <select
                                                                value={
                                                                    months[
                                                                        getMonth(
                                                                            date,
                                                                        )
                                                                    ]
                                                                }
                                                                onChange={({
                                                                    target: {
                                                                        value,
                                                                    },
                                                                }) =>
                                                                    changeMonth(
                                                                        months.indexOf(
                                                                            value,
                                                                        ),
                                                                    )
                                                                }
                                                            >
                                                                {months.map(
                                                                    option => (
                                                                        <option
                                                                            key={
                                                                                option
                                                                            }
                                                                            value={
                                                                                option
                                                                            }
                                                                        >
                                                                            {
                                                                                option
                                                                            }
                                                                        </option>
                                                                    ),
                                                                )}
                                                            </select>
                                                        </div>

                                                        <div
                                                            className={`${styles.calendarSelectWrapper} ${styles.calendarSelectWrapperEnd}`}
                                                        >
                                                            <select
                                                                value={getYear(
                                                                    date,
                                                                )}
                                                                onChange={({
                                                                    target: {
                                                                        value,
                                                                    },
                                                                }) =>
                                                                    changeYear(
                                                                        parseInt(
                                                                            value,
                                                                        ),
                                                                    )
                                                                }
                                                            >
                                                                {years.map(
                                                                    option => (
                                                                        <option
                                                                            key={
                                                                                option
                                                                            }
                                                                            value={
                                                                                option
                                                                            }
                                                                        >
                                                                            {
                                                                                option
                                                                            }
                                                                        </option>
                                                                    ),
                                                                )}
                                                            </select>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.calendarNavButton
                                                            }
                                                            onClick={
                                                                increaseMonth
                                                            }
                                                            disabled={
                                                                nextMonthButtonDisabled
                                                            }
                                                        >
                                                            <i className="bi bi-chevron-right" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.calendarNavButton
                                                            }
                                                            onClick={
                                                                increaseYear
                                                            }
                                                            disabled={
                                                                nextMonthButtonDisabled
                                                            }
                                                        >
                                                            <i className="bi bi-chevron-double-right" />
                                                        </button>
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.calendarFooter}>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={openOccurrenceDrawer}
                                    >
                                        View List
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className={styles.dateTimeRow}>
                            <div className={styles.controlField}>
                                <span className={styles.fieldLabel}>
                                    {getText('msg_fc612a2fcc72')}{' '}
                                    <span className={styles.requiredMark}>
                                        *
                                    </span>
                                </span>
                                <div className={styles.datePickerField}>
                                    <CustomDatePicker
                                        onDateChange={value =>
                                            update('startDate', value)
                                        }
                                        displayFormat="EEEE, MMM dd yyyy"
                                        selectedDate={form.startDate}
                                        minDate={new Date()}
                                        inputClassName={
                                            styles.scheduleDateInput
                                        }
                                    />
                                </div>
                            </div>

                            <div className={styles.controlField}>
                                <span className={styles.fieldLabel}>
                                    {getText('msg_6c82e6dd8680')}{' '}
                                    <span className={styles.requiredMark}>
                                        *
                                    </span>
                                </span>
                                <Dropdown
                                    defaultValue={getDropdownLabel(
                                        TIME_OPTIONS,
                                        form.time,
                                    )}
                                    options={TIME_OPTIONS}
                                    onChange={value => update('time', value)}
                                    height="48px"
                                />
                            </div>

                            <div className={styles.controlField}>
                                <span className={styles.fieldLabel}>
                                    {getText('msg_d1f7dc891b5e')}{' '}
                                    <span className={styles.requiredMark}>
                                        *
                                    </span>
                                </span>
                                <Dropdown
                                    defaultValue={getDropdownLabel(
                                        TIMEZONE_OPTIONS,
                                        form.timezone,
                                    )}
                                    options={TIMEZONE_OPTIONS}
                                    onChange={value =>
                                        update('timezone', value)
                                    }
                                    height="48px"
                                />
                            </div>
                        </div>

                        {form.frequency !== 'once' && (
                            <div className={styles.endsRow}>
                                <div className={styles.endsField}>
                                    <span className={styles.fieldLabel}>
                                        {getText('msg_91856dcb7d93')}{' '}
                                        <span className={styles.requiredMark}>
                                            *
                                        </span>
                                    </span>
                                    <Dropdown
                                        defaultValue={getDropdownLabel(
                                            END_MODE_OPTIONS,
                                            form.endsMode,
                                        )}
                                        options={END_MODE_OPTIONS}
                                        onChange={value =>
                                            update('endsMode', value)
                                        }
                                        height="48px"
                                    />
                                </div>

                                {form.endsMode === 'after' ? (
                                    <div className={styles.occurrenceField}>
                                        <span
                                            className={styles.labelSpacer}
                                            aria-hidden="true"
                                        >
                                            placeholder
                                        </span>

                                        <div
                                            className={styles.occurrenceInline}
                                        >
                                            <label
                                                htmlFor="occurrenceCount"
                                                className="visually-hidden"
                                            >
                                                {getText('msg_0d48f7863835')}
                                            </label>
                                            <input
                                                id="occurrenceCount"
                                                className={`form-control ${styles.occurrenceInput}`}
                                                type="number"
                                                min="1"
                                                max="366"
                                                value={form.occurrenceCount}
                                                onChange={event =>
                                                    update(
                                                        'occurrenceCount',
                                                        event.target.value,
                                                    )
                                                }
                                            />
                                            <span
                                                className={
                                                    styles.occurrenceSuffix
                                                }
                                            >
                                                {getText('msg_0d48f7863835')}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.endsField}>
                                        <span className={styles.fieldLabel}>
                                            {getText('msg_89d10cd6c1e1')}
                                        </span>
                                        <div className={styles.datePickerField}>
                                            <CustomDatePicker
                                                onDateChange={value =>
                                                    update('endDate', value)
                                                }
                                                displayFormat="EEEE, MMM dd yyyy"
                                                selectedDate={form.endDate}
                                                minDate={
                                                    form.startDate
                                                        ? new Date(
                                                              `${form.startDate}T00:00:00`,
                                                          )
                                                        : null
                                                }
                                                inputClassName={
                                                    styles.scheduleDateInput
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {form.frequency === 'daily' && (
                            <fieldset className={styles.recurrencePanel}>
                                <legend className="visually-hidden">
                                    {getText('msg_c67b007cc623')}
                                </legend>

                                <div className={styles.dailyRecurrenceOptions}>
                                    <label
                                        className={styles.dailyRecurrenceRow}
                                    >
                                        <input
                                            className={styles.recurrenceRadio}
                                            type="radio"
                                            name="dailyMode"
                                            checked={
                                                form.dailyMode === 'interval'
                                            }
                                            onChange={() =>
                                                update('dailyMode', 'interval')
                                            }
                                        />
                                        <span
                                            className={
                                                styles.dailyRecurrenceText
                                            }
                                        >
                                            {getText('msg_0a3eeeab9c9b')}
                                        </span>
                                        <input
                                            className={`form-control ${styles.recurrenceNumberInput}`}
                                            type="number"
                                            min="1"
                                            max="366"
                                            value={form.dailyInterval}
                                            onChange={event =>
                                                update(
                                                    'dailyInterval',
                                                    event.target.value,
                                                )
                                            }
                                            disabled={
                                                form.dailyMode !== 'interval'
                                            }
                                        />
                                        <span
                                            className={
                                                styles.dailyRecurrenceText
                                            }
                                        >
                                            {getText('msg_1e0b597ec3c9')}
                                        </span>
                                    </label>

                                    <label
                                        className={styles.dailyRecurrenceRow}
                                    >
                                        <input
                                            className={styles.recurrenceRadio}
                                            type="radio"
                                            name="dailyMode"
                                            checked={
                                                form.dailyMode === 'weekday'
                                            }
                                            onChange={() =>
                                                update('dailyMode', 'weekday')
                                            }
                                        />
                                        <span
                                            className={
                                                styles.dailyRecurrenceText
                                            }
                                        >
                                            {getText('msg_84bc1f467ade')}
                                        </span>
                                        <span
                                            className={
                                                styles.dailyRecurrencePlaceholder
                                            }
                                        />
                                        <span
                                            className={
                                                styles.dailyRecurrencePlaceholder
                                            }
                                        />
                                    </label>
                                </div>
                            </fieldset>
                        )}

                        {form.frequency === 'weekly' && (
                            <fieldset className={styles.weekdays}>
                                <legend>{getText('msg_66bdfe35729f')}</legend>
                                <div className={styles.weekdayGrid}>
                                    {WEEKDAYS.map(([value, label]) => (
                                        <label
                                            key={value}
                                            className={styles.checkboxLabel}
                                        >
                                            <input
                                                type="checkbox"
                                                className={
                                                    styles.broadcastCheckbox
                                                }
                                                checked={form.weekdays.includes(
                                                    value,
                                                )}
                                                onChange={event =>
                                                    update(
                                                        'weekdays',
                                                        event.target.checked
                                                            ? [
                                                                  ...form.weekdays,
                                                                  value,
                                                              ]
                                                            : form.weekdays.filter(
                                                                  day =>
                                                                      day !==
                                                                      value,
                                                              ),
                                                    )
                                                }
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            </fieldset>
                        )}

                        {form.frequency === 'monthly' && (
                            <fieldset className={styles.weekdays}>
                                <legend>{getText('msg_e0c2c6f69efc')}</legend>

                                <div
                                    className={styles.monthlyRecurrenceOptions}
                                >
                                    <label
                                        className={`${styles.checkboxLabel} ${styles.monthlyRecurrenceRow}`}
                                    >
                                        <span
                                            className={
                                                styles.monthlyRecurrenceRadio
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="monthlyMode"
                                                className={
                                                    styles.monthlyRadioInput
                                                }
                                                checked={
                                                    form.monthlyMode === 'day'
                                                }
                                                onChange={() =>
                                                    update('monthlyMode', 'day')
                                                }
                                            />
                                        </span>
                                        <span
                                            className={
                                                styles.monthlyRecurrenceContent
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.monthlyRecurrenceMainLine
                                                }
                                            >
                                                {getText('msg_0aa8e85658e8')}
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    min="1"
                                                    max="31"
                                                    value={form.monthDay}
                                                    onChange={event =>
                                                        update(
                                                            'monthDay',
                                                            event.target.value,
                                                        )
                                                    }
                                                    disabled={
                                                        form.monthlyMode !==
                                                        'day'
                                                    }
                                                    style={{ width: '96px' }}
                                                />
                                                {getText('msg_a7cb229e0458')}
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    min="1"
                                                    max="12"
                                                    value={form.monthlyInterval}
                                                    onChange={event =>
                                                        update(
                                                            'monthlyInterval',
                                                            event.target.value,
                                                        )
                                                    }
                                                    style={{ width: '96px' }}
                                                />
                                            </span>
                                            <span
                                                className={
                                                    styles.monthlyRecurrenceSuffix
                                                }
                                            >
                                                {getText('msg_f1daeded0e35')}
                                            </span>
                                        </span>
                                    </label>

                                    <label
                                        className={`${styles.checkboxLabel} ${styles.monthlyRecurrenceRow}`}
                                    >
                                        <span
                                            className={
                                                styles.monthlyRecurrenceRadio
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="monthlyMode"
                                                className={
                                                    styles.monthlyRadioInput
                                                }
                                                checked={
                                                    form.monthlyMode ===
                                                    'ordinal'
                                                }
                                                onChange={() =>
                                                    update(
                                                        'monthlyMode',
                                                        'ordinal',
                                                    )
                                                }
                                            />
                                        </span>
                                        <span
                                            className={
                                                styles.monthlyRecurrenceContent
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.monthlyRecurrenceMainLine
                                                }
                                            >
                                                {getText('msg_96a82b6dbc6e')}
                                                <select
                                                    className="form-select"
                                                    value={form.monthlyOrdinal}
                                                    onChange={event =>
                                                        update(
                                                            'monthlyOrdinal',
                                                            event.target.value,
                                                        )
                                                    }
                                                    disabled={
                                                        form.monthlyMode !==
                                                        'ordinal'
                                                    }
                                                    style={{ width: '140px' }}
                                                >
                                                    {ORDINAL_OPTIONS.map(
                                                        option => (
                                                            <option
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                            >
                                                                {option.label}
                                                            </option>
                                                        ),
                                                    )}
                                                </select>
                                                <select
                                                    className="form-select"
                                                    value={form.monthlyWeekday}
                                                    onChange={event =>
                                                        update(
                                                            'monthlyWeekday',
                                                            event.target.value,
                                                        )
                                                    }
                                                    disabled={
                                                        form.monthlyMode !==
                                                        'ordinal'
                                                    }
                                                    style={{ width: '160px' }}
                                                >
                                                    {MONTHLY_WEEKDAY_OPTIONS.map(
                                                        option => (
                                                            <option
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                            >
                                                                {option.label}
                                                            </option>
                                                        ),
                                                    )}
                                                </select>
                                                {getText('msg_a7cb229e0458')}
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    min="1"
                                                    max="12"
                                                    value={form.monthlyInterval}
                                                    onChange={event =>
                                                        update(
                                                            'monthlyInterval',
                                                            event.target.value,
                                                        )
                                                    }
                                                    style={{ width: '96px' }}
                                                />
                                            </span>
                                            <span
                                                className={
                                                    styles.monthlyRecurrenceSuffix
                                                }
                                            >
                                                {getText('msg_f1daeded0e35')}
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            </fieldset>
                        )}

                        {isOccurrenceDrawerOpen && (
                            <>
                                <div
                                    className={styles.drawerOverlay}
                                    onClick={() =>
                                        setIsOccurrenceDrawerOpen(false)
                                    }
                                />
                                <div className={styles.drawer}>
                                    <div className={styles.drawerHeader}>
                                        <div>
                                            <h3 className={styles.drawerTitle}>
                                                {getText('msg_574b1c110d9e')}
                                            </h3>
                                            <p
                                                className={
                                                    styles.drawerSubtitle
                                                }
                                            >
                                                {getText('msg_ac8ce0cd8b18')}
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className={
                                                styles.headerActionButton
                                            }
                                            onClick={() =>
                                                setIsOccurrenceDrawerOpen(false)
                                            }
                                        >
                                            <i className="bi bi-x me-2" />
                                            {getText('msg_bbfa773e5a63')}
                                        </button>
                                    </div>

                                    <div className={styles.drawerBody}>
                                        {drawerOccurrences.length === 0 ? (
                                            <p className={styles.drawerEmpty}>
                                                {getText('msg_551867acae5c')}
                                            </p>
                                        ) : (
                                            drawerOccurrences.map(
                                                (occurrence, index) => (
                                                    <div
                                                        key={`${occurrence}-${index}`}
                                                        className={
                                                            styles.drawerRow
                                                        }
                                                    >
                                                        <input
                                                            className={`form-control ${styles.drawerInput}`}
                                                            type="datetime-local"
                                                            value={toUtcDateTimeInputValue(
                                                                occurrence,
                                                            )}
                                                            onChange={event =>
                                                                updateDrawerOccurrence(
                                                                    index,
                                                                    event.target
                                                                        .value,
                                                                )
                                                            }
                                                        />

                                                        <button
                                                            type="button"
                                                            className="button-base button-secondary"
                                                            onClick={() =>
                                                                removeDrawerOccurrence(
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            {getText(
                                                                'msg_e963907dac5c',
                                                            )}
                                                        </button>
                                                    </div>
                                                ),
                                            )
                                        )}
                                    </div>

                                    <div className={styles.drawerFooter}>
                                        <button
                                            type="button"
                                            className="button-base ing-button-secondary-left"
                                            onClick={addDrawerOccurrence}
                                        >
                                            {getText('msg_60b4aa0c50b0')}
                                        </button>

                                        <div
                                            className={
                                                styles.drawerFooterActions
                                            }
                                        >
                                            <button
                                                type="button"
                                                className="button-base button-secondary"
                                                onClick={() =>
                                                    setIsOccurrenceDrawerOpen(
                                                        false,
                                                    )
                                                }
                                            >
                                                {getText('msg_77dfd2135f4d')}
                                            </button>

                                            <button
                                                type="button"
                                                className="button-base ing-button-primary-left"
                                                onClick={applyOccurrenceReview}
                                                disabled={
                                                    !drawerOccurrences.length
                                                }
                                            >
                                                {getText('msg_b9e6403e1da7')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className={styles.footer}>
                            <button
                                className={`button-base button-secondary ${styles.footerButton}`}
                                type="button"
                                onClick={() =>
                                    navigate(
                                        ROUTE_BUILDERS.notificationScheduleList(
                                            notificationId,
                                        ),
                                    )
                                }
                            >
                                {getText('msg_77dfd2135f4d')}
                            </button>

                            <button
                                className={`button-base ing-button-primary-left ${styles.footerButton}`}
                                type="submit"
                                disabled={
                                    isSaving ||
                                    scheduleDefinitionsLoading ||
                                    scheduleDefinitionsError ||
                                    definitionLoadError
                                }
                            >
                                {isSaving
                                    ? getText('msg_ae7e887517b0')
                                    : scheduleDefinitionsLoading
                                      ? getText('msg_1abe79290afc')
                                      : getText('msg_0a8adac9d6d5')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NotificationScheduleDetails;
